import { useEffect } from "react";

/**
 * The living mood-and-time layer.
 *
 * A continuous, almost-subliminal drift of the page's warmth and light with two
 * things: the real time of evening (warmer and dimmer as it gets later, so by
 * the final page the room itself feels tucked in), and how far we are through
 * the story (each page settles a little deeper than the last). Felt, never
 * announced — it writes a single `--mood-dusk` (0..1) onto :root; the CSS does
 * the rest, and keeps it gentle so reading contrast is never harmed.
 *
 * It updates on mount, whenever progress changes, and slowly on a timer so a
 * long bedtime read visibly tucks in even on one page.
 */

/** Evening depth from the wall clock: a cozy daytime floor, ramping into night. */
function duskFromClock(date: Date): number {
  const h = date.getHours() + date.getMinutes() / 60;
  // Deep night (after 22:00 or before 05:00) sits at its warmest/dimmest.
  if (h >= 22 || h < 5) return 0.85;
  // Evening ramp from ~16:00 → 22:00.
  const eveningness = Math.min(1, Math.max(0, (h - 16) / (22 - 16)));
  // A warm floor so even an afternoon read still feels like a lamp-lit nook.
  return Math.max(0.12, eveningness * 0.85);
}

/** Combine clock dusk with story progress (0..1) into one capped value. */
export function computeDusk(date: Date, progress: number): number {
  const base = duskFromClock(date);
  const p = Math.min(1, Math.max(0, progress));
  return Math.min(0.92, base + p * 0.12);
}

/**
 * Drive `--mood-dusk` on the document root.
 *
 * @param progress 0..1 through the story (page index / last index).
 * @param active   while false (e.g. the cover) we hold a gentle threshold value.
 */
export function useMood(progress: number, active: boolean): void {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;

    const apply = () => {
      const dusk = active ? computeDusk(new Date(), progress) : duskFromClock(new Date());
      root.style.setProperty("--mood-dusk", dusk.toFixed(3));
      // The phone's own chrome tucks in too: drift the browser theme-color
      // from warm cream toward deep dusk. Felt, never announced.
      const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
      if (meta) {
        const mix = (a: number, b: number) => Math.round(a + (b - a) * dusk);
        meta.content = `rgb(${mix(243, 36)}, ${mix(233, 28)}, ${mix(219, 41)})`;
      }
    };

    apply();
    // Re-evaluate slowly so a long read tucks the room in over real time.
    const id = window.setInterval(apply, 60_000);
    return () => window.clearInterval(id);
  }, [progress, active]);
}
